import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Task, TaskStatus, Priority } from '../types';
import { showToast } from '../utils/toast';

type DbTask = Database['public']['Tables']['tasks']['Row'];
type DbTaskInsert = Database['public']['Tables']['tasks']['Insert'];
type DbTaskUpdate = Database['public']['Tables']['tasks']['Update'];

const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

const safeUUID = (id?: string | null) => (id && isValidUUID(id) ? id : null);

const isDateToday = (dateString?: string | null) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/** Normalise une priorité (LLM ou UI) vers une valeur acceptée par l'enum Supabase item_priority. */
const normalizePriority = (priority?: string | null): 'low' | 'medium' | 'high' | 'critical' => {
  if (!priority) return 'medium';
  const p = priority.toLowerCase().trim();
  if (p === 'low' || p === 'basse' || p === 'faible') return 'low';
  if (p === 'medium' || p === 'moyenne' || p === 'normale' || p === 'normal') return 'medium';
  if (p === 'high' || p === 'haute' || p === 'élevée' || p === 'elevee') return 'high';
  if (p === 'critical' || p === 'critique' || p === 'urgent' || p === 'urgente') return 'critical';
  return 'medium';
};

const mapToTask = (dbTask: DbTask): Task => {
  return {
    id: dbTask.id,
    title: dbTask.title,
    description: dbTask.description || undefined,
    status: (dbTask.status as TaskStatus) || 'todo',
    priority: (dbTask.priority as Priority) || 'medium',
    domainId: dbTask.domain_id || '', // Keep UI compatibility
    dueDate: dbTask.due_date || undefined,
    isToday: isDateToday(dbTask.due_date),
    tags: dbTask.tags || [],
    goalId: dbTask.goal_id || undefined,
    eventId: dbTask.event_id || undefined,
    parentId: dbTask.parent_id || undefined,
    estimatedDuration: dbTask.estimated_duration || undefined,
    cost: dbTask.cost ?? null,
    completedAt: dbTask.completed_at ?? null,
  };
};

export const taskService = {
  async getTasks(): Promise<Task[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[task.service] getTasks:', error.message);
        showToast('Erreur lors du chargement des tâches', 'error');
        return [];
      }
      return (data || []).map(mapToTask);
    } catch (err: any) {
      console.error('[task.service] getTasks:', err.message);
      showToast('Erreur lors du chargement des tâches', 'error');
      return [];
    }
  },

  async createTask(task: Partial<Task>): Promise<Task> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté pour créer une tâche.');

    const insertData: DbTaskInsert = {
      user_id: user.id,
      title: task.title || 'Nouvelle tâche',
      description: task.description || null,
      status: (task.status as DbTaskInsert['status']) || 'todo',
      priority: normalizePriority(task.priority),
      domain_id: safeUUID(task.domainId),
      due_date: task.dueDate || null,
      tags: task.tags || [],
      goal_id: safeUUID(task.goalId),
      event_id: safeUUID(task.eventId),
      parent_id: safeUUID(task.parentId),
      estimated_duration: task.estimatedDuration || null,
      cost: task.cost ?? null,
    };

    const { data, error } = await supabase.from('tasks').insert(insertData).select().single();

    if (error) {
      console.error('[task.service] createTask:', error.message);
      showToast('Impossible de créer la tâche', 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer la tâche : l'objectif ou le domaine lié n'existe pas encore dans la base de données Supabase. Veuillez le retirer ou utiliser un élément déjà synchronisé."
        );
      }
      throw error;
    }
    return mapToTask(data);
  },

  async updateTask(id: string, updates: Partial<Task>): Promise<Task> {
    const updateData: DbTaskUpdate = {
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.description !== undefined && { description: updates.description }),
      ...(updates.status !== undefined && { status: updates.status as DbTaskUpdate['status'] }),
      ...(updates.priority !== undefined && {
        priority: normalizePriority(updates.priority),
      }),
      ...(updates.domainId !== undefined && { domain_id: safeUUID(updates.domainId) }),
      ...(updates.dueDate !== undefined && { due_date: updates.dueDate }),
      ...(updates.tags !== undefined && { tags: updates.tags }),
      ...(updates.goalId !== undefined && { goal_id: safeUUID(updates.goalId) }),
      ...(updates.eventId !== undefined && { event_id: safeUUID(updates.eventId) }),
      ...(updates.parentId !== undefined && { parent_id: safeUUID(updates.parentId) }),
      ...(updates.estimatedDuration !== undefined && {
        estimated_duration: updates.estimatedDuration || null,
      }),
      ...(updates.cost !== undefined && { cost: updates.cost ?? null }),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('tasks')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[task.service] updateTask:', error.message);
      showToast('Impossible de mettre à jour la tâche', 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer la tâche : l'objectif ou le domaine lié n'existe pas encore dans la base de données Supabase. Veuillez le retirer ou utiliser un élément déjà synchronisé."
        );
      }
      throw error;
    }
    return mapToTask(data);
  },

  async deleteTask(id: string): Promise<void> {
    const { error } = await supabase.from('tasks').delete().eq('id', id);

    if (error) {
      console.error('[task.service] deleteTask:', error.message);
      showToast('Impossible de supprimer la tâche', 'error');
      throw error;
    }
  },
};
